import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, Eye, EyeOff, MapPin, Plane, Globe2, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImage, formatApiErrorDetail } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";

const emptyPin = {
  kind: "world",
  title: "",
  region: "",
  lat: 0,
  lng: 0,
  photo: "",
  story: "",
  date_label: "Arrived",
  status: "arrived",
  is_visible: true,
  sort_order: 0,
};

const PinForm = ({ pin, onCancel, onSaved }) => {
  const isNew = !pin.id;
  const [form, setForm] = useState({ ...emptyPin, ...pin });
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const geocode = async () => {
    if (!form.title.trim()) {
      toast.error("Type a city / place name first.");
      return;
    }
    setGeocoding(true);
    try {
      const q = form.region ? `${form.title}, ${form.region}` : form.title;
      const { data } = await api.get(`/admin/journey/geocode?q=${encodeURIComponent(q)}`);
      if (data.found) {
        setForm((f) => ({ ...f, lat: data.lat, lng: data.lng }));
        toast.success(`Located: ${data.display_name.split(",").slice(0, 3).join(",")}`);
      } else {
        toast.error("Couldn't find that place. Try adding country/state to be more specific, or enter lat/lng manually.");
      }
    } catch {
      toast.error("Geocoding failed. You can still enter lat/lng manually.");
    } finally {
      setGeocoding(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    if (!form.lat || !form.lng) {
      toast.error('Please use "Find on map" to set the location, or enter lat/lng.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, lat: parseFloat(form.lat), lng: parseFloat(form.lng), sort_order: parseInt(form.sort_order || 0, 10) };
      if (isNew) {
        await api.post("/admin/journey/pins", payload);
        toast.success("Pin added");
      } else {
        await api.put(`/admin/journey/pins/${pin.id}`, payload);
        toast.success("Pin updated");
      }
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-rule bg-paper-2/30 p-6 space-y-5" data-testid="journey-pin-form">
      <div className="flex items-center gap-3">
        <label className="text-xs uppercase tracking-[0.2em] text-ink-soft">Map:</label>
        <button
          type="button"
          onClick={() => update("kind", "world")}
          data-testid="pin-kind-world"
          className={`px-3 py-1.5 text-xs tracking-[0.18em] uppercase border ${form.kind === "world" ? "bg-ink text-paper border-ink" : "border-rule text-ink-soft"}`}
        >
          <Globe2 size={12} className="inline mr-1.5" /> World
        </button>
        <button
          type="button"
          onClick={() => update("kind", "usa")}
          data-testid="pin-kind-usa"
          className={`px-3 py-1.5 text-xs tracking-[0.18em] uppercase border ${form.kind === "usa" ? "bg-ink text-paper border-ink" : "border-rule text-ink-soft"}`}
        >
          🇺🇸 USA
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">Title (City, etc.)</label>
          <input
            value={form.title}
            onChange={(e) => update("title", e.target.value)}
            data-testid="pin-title-input"
            className="w-full bg-transparent border-b border-rule focus:border-ink py-2 font-serif-display text-lg text-ink outline-none"
            placeholder={form.kind === "world" ? "London, UK" : "Austin, Texas"}
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">Country / State</label>
          <input
            value={form.region}
            onChange={(e) => update("region", e.target.value)}
            data-testid="pin-region-input"
            className="w-full bg-transparent border-b border-rule focus:border-ink py-2 text-sm text-ink outline-none"
            placeholder={form.kind === "world" ? "United Kingdom" : "Texas"}
          />
        </div>
      </div>

      <div className="flex items-end gap-3">
        <div className="flex-1 grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">Latitude</label>
            <input
              type="number"
              step="any"
              value={form.lat}
              onChange={(e) => update("lat", e.target.value)}
              data-testid="pin-lat-input"
              className="w-full bg-transparent border-b border-rule focus:border-ink py-2 text-sm text-ink outline-none"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">Longitude</label>
            <input
              type="number"
              step="any"
              value={form.lng}
              onChange={(e) => update("lng", e.target.value)}
              data-testid="pin-lng-input"
              className="w-full bg-transparent border-b border-rule focus:border-ink py-2 text-sm text-ink outline-none"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={geocode}
          disabled={geocoding}
          data-testid="pin-geocode-btn"
          className="inline-flex items-center gap-2 px-4 py-2 border border-ink text-ink text-xs tracking-[0.18em] uppercase hover:bg-ink hover:text-paper transition-colors disabled:opacity-50"
        >
          {geocoding ? <Loader2 className="animate-spin" size={12} /> : <MapPin size={12} />} Find on map
        </button>
      </div>

      <ImageUploader
        value={form.photo}
        onChange={(v) => update("photo", v)}
        label="Photo (optional)"
        testIdPrefix="pin-photo"
        helper="A picture of the book in its new home, or the recipient holding it."
      />

      <div>
        <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">Story / Caption (optional)</label>
        <textarea
          value={form.story}
          onChange={(e) => update("story", e.target.value)}
          data-testid="pin-story-input"
          rows={2}
          className="w-full bg-paper-2/40 border border-rule focus:border-ink p-3 text-sm text-ink outline-none italic"
          placeholder="A line or two about who has it and how it arrived…"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">Date Label</label>
          <input
            value={form.date_label}
            onChange={(e) => update("date_label", e.target.value)}
            data-testid="pin-date-input"
            className="w-full bg-transparent border-b border-rule focus:border-ink py-2 text-sm text-ink outline-none"
            placeholder="April 2026 / On the way"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">Status</label>
          <select
            value={form.status}
            onChange={(e) => update("status", e.target.value)}
            data-testid="pin-status-input"
            className="w-full bg-transparent border-b border-rule focus:border-ink py-2 text-sm text-ink outline-none"
          >
            <option value="arrived">📍 Arrived</option>
            <option value="on_the_way">✈️ On the way</option>
          </select>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">Sort order</label>
          <input
            type="number"
            value={form.sort_order}
            onChange={(e) => update("sort_order", e.target.value)}
            data-testid="pin-sort-input"
            className="w-full bg-transparent border-b border-rule focus:border-ink py-2 text-sm text-ink outline-none"
          />
        </div>
      </div>

      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={form.is_visible}
          onChange={(e) => update("is_visible", e.target.checked)}
          data-testid="pin-visible-toggle"
          className="w-4 h-4"
        />
        <span className="text-sm text-ink">Visible on public page</span>
      </label>

      <div className="flex flex-wrap gap-3 pt-2 border-t border-rule">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="pin-save-btn"
          className="inline-flex items-center gap-2 px-6 py-3 bg-terracotta text-paper text-xs tracking-[0.2em] uppercase hover:bg-ink transition-colors disabled:opacity-50"
        >
          <Save size={14} /> {saving ? "Saving…" : isNew ? "Add pin" : "Update pin"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid="pin-cancel-btn"
          className="px-6 py-3 border border-ink text-ink text-xs tracking-[0.2em] uppercase hover:bg-ink hover:text-paper transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const SettingsForm = ({ settings, onSaved }) => {
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/journey/settings", form);
      toast.success(data.is_published ? "Page is now LIVE!" : "Saved (still in draft)");
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="border border-rule bg-paper-2/30 p-6 space-y-5" data-testid="journey-settings-form">
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={!!form.is_published}
          onChange={(e) => update("is_published", e.target.checked)}
          data-testid="journey-publish-toggle"
          className="w-5 h-5 mt-1"
        />
        <span>
          <span className="text-sm text-ink font-medium">
            Publish &quot;Lucy&apos;s Journey&quot; page
          </span>
          <span className="block text-xs text-ink-soft mt-1">
            When OFF, only you can see this page. When ON, it appears in your menu and is public.
          </span>
        </span>
      </label>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">Main heading</label>
          <input
            value={form.heading}
            onChange={(e) => update("heading", e.target.value)}
            className="w-full bg-transparent border-b border-rule focus:border-ink py-2 font-serif-display text-xl text-ink outline-none"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">Intro</label>
          <textarea
            value={form.intro}
            onChange={(e) => update("intro", e.target.value)}
            rows={2}
            className="w-full bg-paper-2/40 border border-rule focus:border-ink p-3 text-sm text-ink outline-none italic"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">World map heading</label>
          <input
            value={form.world_heading}
            onChange={(e) => update("world_heading", e.target.value)}
            className="w-full bg-transparent border-b border-rule focus:border-ink py-2 text-lg text-ink outline-none"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">World map intro</label>
          <textarea
            value={form.world_intro}
            onChange={(e) => update("world_intro", e.target.value)}
            rows={2}
            className="w-full bg-paper-2/40 border border-rule focus:border-ink p-3 text-sm text-ink outline-none italic"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">USA map heading</label>
          <input
            value={form.usa_heading}
            onChange={(e) => update("usa_heading", e.target.value)}
            className="w-full bg-transparent border-b border-rule focus:border-ink py-2 text-lg text-ink outline-none"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">USA map intro</label>
          <textarea
            value={form.usa_intro}
            onChange={(e) => update("usa_intro", e.target.value)}
            rows={2}
            className="w-full bg-paper-2/40 border border-rule focus:border-ink p-3 text-sm text-ink outline-none italic"
          />
        </div>
      </div>

      <div className="pt-3 border-t border-rule">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="journey-settings-save-btn"
          className="inline-flex items-center gap-2 px-6 py-3 bg-terracotta text-paper text-xs tracking-[0.2em] uppercase hover:bg-ink transition-colors disabled:opacity-50"
        >
          <Save size={14} /> {saving ? "Saving…" : "Save page settings"}
        </button>
      </div>
    </div>
  );
};

export default function AdminJourney() {
  const [pins, setPins] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = none, "new" or pin object

  const load = async () => {
    setLoading(true);
    try {
      const [pinsRes, settingsRes] = await Promise.all([
        api.get("/admin/journey/pins"),
        api.get("/admin/journey/settings"),
      ]);
      setPins(pinsRes.data || []);
      setSettings(settingsRes.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load journey");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const removePin = async (id) => {
    if (!window.confirm("Delete this pin? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/journey/pins/${id}`);
      toast.success("Pin deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not delete");
    }
  };

  const worldPins = pins.filter((p) => p.kind === "world");
  const usaPins = pins.filter((p) => p.kind === "usa");

  return (
    <div className="bg-paper min-h-[80vh]" data-testid="admin-journey-page">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-terracotta mb-8"
          data-testid="admin-journey-back-link"
        >
          <ArrowLeft size={14} /> Dashboard
        </Link>

        <div className="border-b border-rule pb-6 mb-10">
          <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
            Lucy&apos;s Journey
          </p>
          <h1 className="font-serif-display text-3xl lg:text-4xl text-ink tracking-tight">
            Track where Lucy&apos;s book has traveled
          </h1>
          <p className="text-sm text-ink-soft mt-2 max-w-xl">
            Manage the two maps (World + USA). Add a pin each time a copy of Lucy lands somewhere new.
            The page stays hidden until you toggle &quot;Publish&quot; below.
          </p>
        </div>

        {loading ? (
          <p className="text-ink-soft">Loading…</p>
        ) : (
          <>
            <h2 className="font-serif-display text-2xl text-ink mb-4">Page settings</h2>
            <SettingsForm settings={settings} onSaved={setSettings} />

            <div className="mt-14 flex items-end justify-between gap-4 border-b border-rule pb-5">
              <h2 className="font-serif-display text-2xl text-ink">Pins ({pins.length})</h2>
              {!editing && (
                <button
                  onClick={() => setEditing({ ...emptyPin })}
                  data-testid="journey-new-pin-btn"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-ink text-paper text-xs tracking-[0.2em] uppercase hover:bg-terracotta transition-colors"
                >
                  <Plus size={14} /> Add pin
                </button>
              )}
            </div>

            {editing && (
              <div className="mt-6">
                <PinForm
                  pin={editing}
                  onCancel={() => setEditing(null)}
                  onSaved={() => { setEditing(null); load(); }}
                />
              </div>
            )}

            {!editing && (
              <>
                <div className="mt-8">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-sage mb-3">🌍 World ({worldPins.length})</p>
                  <ul className="divide-y divide-rule">
                    {worldPins.map((p, i) => (
                      <PinRow key={p.id} pin={p} idx={i} onEdit={() => setEditing(p)} onDelete={() => removePin(p.id)} />
                    ))}
                    {worldPins.length === 0 && (
                      <li className="py-4 text-sm italic text-ink-soft">No world pins yet.</li>
                    )}
                  </ul>
                </div>

                <div className="mt-10">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-sage mb-3">🇺🇸 USA ({usaPins.length})</p>
                  <ul className="divide-y divide-rule">
                    {usaPins.map((p, i) => (
                      <PinRow key={p.id} pin={p} idx={i} onEdit={() => setEditing(p)} onDelete={() => removePin(p.id)} />
                    ))}
                    {usaPins.length === 0 && (
                      <li className="py-4 text-sm italic text-ink-soft">No USA pins yet.</li>
                    )}
                  </ul>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PinRow({ pin, idx, onEdit, onDelete }) {
  return (
    <li className="py-4 flex items-center gap-4" data-testid={`journey-pin-row-${pin.kind}-${idx}`}>
      {pin.photo ? (
        <img src={resolveImage(pin.photo)} alt="" className="w-12 h-12 object-cover border border-rule shrink-0" />
      ) : (
        <div className="w-12 h-12 border border-rule bg-paper-2/40 flex items-center justify-center shrink-0">
          {pin.status === "on_the_way" ? <Plane size={16} className="text-ink-soft" /> : <MapPin size={16} className="text-ink-soft" />}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-serif-display text-lg text-ink truncate">{pin.title}</p>
        <p className="text-xs text-ink-soft truncate">
          {pin.region && <span>{pin.region} · </span>}
          {pin.lat.toFixed(2)}, {pin.lng.toFixed(2)}
          {!pin.is_visible && <span className="ml-2 inline-flex items-center gap-1 text-terracotta"><EyeOff size={11} /> Hidden</span>}
          {pin.status === "on_the_way" && <span className="ml-2 text-sage">✈️ On the way</span>}
        </p>
      </div>
      <button onClick={onEdit} className="p-2 border border-rule text-ink-soft hover:text-ink hover:border-ink" title="Edit"><Pencil size={14} /></button>
      <button onClick={onDelete} className="p-2 border border-rule text-ink-soft hover:text-terracotta hover:border-terracotta" title="Delete"><Trash2 size={14} /></button>
    </li>
  );
}
